import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * AI世界选择状态管理
 * 自动记忆用户选择的世界ID，切换页面时自动加载
 */

interface WorldContextType {
  // 当前选中的世界实例ID
  currentWorldInstanceId: string;
  // 当前选中的世界基本信息
  currentWorldInfo: {
    id: number;
    world_id: string;
    name: string;
    status: string;
  } | null;
  // 设置当前世界
  setCurrentWorld: (worldId: string, worldInfo?: any) => void;
  // 清除当前选择
  clearCurrentWorld: () => void;
  // 是否已选择世界
  hasSelectedWorld: boolean;
}

const WorldContext = createContext<WorldContextType | undefined>(undefined);

// localStorage 键名
const STORAGE_KEY = 'o2world_selected_world';

interface WorldProviderProps {
  children: ReactNode;
}

export const WorldProvider: React.FC<WorldProviderProps> = ({ children }) => {
  const [currentWorldInstanceId, setCurrentWorldInstanceId] = useState<string>('');
  const [currentWorldInfo, setCurrentWorldInfo] = useState<any>(null);

  // 初始化：从 localStorage 读取之前的选择
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { worldId, worldInfo } = JSON.parse(saved);
        setCurrentWorldInstanceId(worldId);
        setCurrentWorldInfo(worldInfo);
      }
    } catch (error) {
      console.warn('读取保存的世界选择失败:', error);
      // 清除无效数据
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // 设置当前世界
  const setCurrentWorld = (worldId: string, worldInfo?: any) => {
    setCurrentWorldInstanceId(worldId);

    if (worldInfo) {
      setCurrentWorldInfo(worldInfo);
      // 保存到 localStorage
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          worldId,
          worldInfo,
          timestamp: Date.now()
        })
      );
    }
  };

  // 清除当前选择
  const clearCurrentWorld = () => {
    setCurrentWorldInstanceId('');
    setCurrentWorldInfo(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const hasSelectedWorld = Boolean(currentWorldInstanceId);

  const value: WorldContextType = {
    currentWorldInstanceId,
    currentWorldInfo,
    setCurrentWorld,
    clearCurrentWorld,
    hasSelectedWorld,
  };

  return (
    <WorldContext.Provider value={value}>
      {children}
    </WorldContext.Provider>
  );
};

// Hook：使用 World Context
export const useWorld = (): WorldContextType => {
  const context = useContext(WorldContext);
  if (context === undefined) {
    throw new Error('useWorld must be used within a WorldProvider');
  }
  return context;
};

export default WorldContext;
